import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image/Image'
import NumberRating from 'components/Common/NumberRating'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'

export const CAROUSEL_CARD_WIDTH = 320

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: ${rem(200)} 1fr;
  gap: ${rem(16)};
  height: 100%;
  position: relative;
`

const BookmarkContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  right: ${rem(12)};
`

const Partnership = styled(PartnershipLabel)`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(12)};
`

function isString(toCheck: string | JSX.Element) {
  return typeof toCheck === 'string'
}

interface Props {
  bookmarkButton?: JSX.Element;
  description?: string | JSX.Element;
  descriptionLines?: number;
  flightPricingDetails?: JSX.Element;
  image: App.Image;
  location: string | JSX.Element;
  title: string;
  offerPriceDetails?: JSX.Element;
  offerUrl: string;
  onClick?: () => void;
  productType?: App.ProductType;
  productTypeKind?: 'default' | 'plain';
  reviews?: {
    showReviews: boolean;
    rating?: number;
    reviewsTotal?: number;
    ratingSubtitle?: string;
  }
  urgencyLabels?: JSX.Element;
  velocityFeatures?: { canEarn: boolean, canBurn: boolean, variant?: 'default' | 'vendor' | 'icon' };
}

function CarouselCard({
  bookmarkButton,
  description,
  descriptionLines,
  flightPricingDetails,
  image,
  location,
  title,
  offerPriceDetails,
  offerUrl,
  onClick,
  productType,
  productTypeKind,
  reviews,
  urgencyLabels,
  velocityFeatures,
}: Props) {
  const showNumberRating = reviews && reviews.showReviews && reviews.rating

  return (
    <CarouselCardContainer
      onClick={onClick}
      target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
      to={offerUrl}
    >
      <Image aspectRatio="3:2" image={image} fit="center" dpr={2} height="200" width="320"/>
      <Group direction="vertical" verticalAlign="space-between" gap={16}>
        <VerticalSpacer gap={8}>
          {urgencyLabels}
          <Group direction="vertical" gap={4}>
            {isString(location) ? <Caption variant="large">{location}</Caption> : location}
            <Heading lineClamp={3} variant="heading6">{title}</Heading>
          </Group>
          {productType && <ProductTypeLabel productType={productType} kind={productTypeKind ?? 'plain'} />}
          {showNumberRating && (
            <NumberRating
              variant="xs"
              rating={reviews.rating!}
              total={reviews.reviewsTotal}
              ratingSubtitle={reviews.ratingSubtitle}
              inlineLabel
            />
          )}
          {description && isString(description) && <BodyText lineClamp={descriptionLines || 2} variant="medium">{description}</BodyText>}
          {description && !isString(description) && description}
        </VerticalSpacer>
        <Group direction="vertical" gap={16}>
          {offerPriceDetails}
          {flightPricingDetails}
        </Group>
      </Group>
      {velocityFeatures && <Partnership canEarn={velocityFeatures.canEarn} canUse={velocityFeatures.canBurn} variant="icon" />}
      <BookmarkContainer>{bookmarkButton}</BookmarkContainer>
    </CarouselCardContainer>
  )
}

export default CarouselCard
